import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Check, ChevronLeft, ChevronRight, Search, Upload, FileText,
    Sparkles, CheckCircle2, AlertCircle, Clock3, ArrowRight, Plus,
} from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import DocumentExtractionPanel from "@/components/idp/DocumentExtractionPanel";
import { api, apiGet, apiPost } from "@/lib/api";
import pt from "@/lib/i18n";
import { formatAOAFull } from "@/lib/sla";

const STEPS = [
    { id: 1, label: pt.wizard.step1, key: "client" },
    { id: 2, label: pt.wizard.step2, key: "docs" },
    { id: 3, label: pt.wizard.step3, key: "service" },
    { id: 4, label: pt.wizard.step4, key: "review" },
    { id: 5, label: pt.wizard.step5, key: "confirm" },
];

const DOC_TYPES = [
    { value: "bi", label: "Bilhete de Identidade" },
    { value: "nif", label: "NIF" },
    { value: "passaporte", label: "Passaporte" },
    { value: "comprovativo_morada", label: "Comprovativo de Morada" },
    { value: "outro", label: "Outro" },
];

const Stepper = ({ current }) => (
    <div className="flex items-center justify-between gap-2 mb-7" data-testid="wizard-stepper">
        {STEPS.map((s, i) => {
            const active = current === s.id;
            const done = current > s.id;
            return (
                <div key={s.id} className="flex items-center flex-1 gap-2">
                    <div
                        className={
                            "h-9 w-9 rounded-full grid place-items-center font-body font-semibold text-xs transition-all flex-shrink-0 " +
                            (done
                                ? "bg-[color:var(--success)] text-white"
                                : active
                                  ? "bg-[color:var(--copper)] text-white shadow-[0_0_0_4px_var(--copper-glow)]"
                                  : "bg-[color:var(--bg-surface)] text-[color:var(--slate-mid)] border border-[color:var(--glass-border)]")
                        }
                    >
                        {done ? <Check size={14} /> : s.id}
                    </div>
                    <span
                        className={
                            "font-body text-xs uppercase tracking-[0.12em] " +
                            (active
                                ? "text-[color:var(--copper-dark)] font-semibold"
                                : "text-[color:var(--slate-light)]")
                        }
                    >
                        {s.label}
                    </span>
                    {i < STEPS.length - 1 && (
                        <div className="flex-1 h-px bg-[color:var(--glass-border)] mx-2" />
                    )}
                </div>
            );
        })}
    </div>
);

const Timer = ({ startedAt }) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
    const m = Math.floor(elapsed / 60).toString().padStart(2, "0");
    const s = (elapsed % 60).toString().padStart(2, "0");
    const ok = elapsed <= 90;
    return (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-card" style={{ borderRadius: 999 }} data-testid="onboarding-timer">
            <Clock3 size={14} className={ok ? "text-[color:var(--success)]" : "text-[color:var(--copper-dark)]"} />
            <span className="font-mono text-sm text-[color:var(--slate-deep)]">{m}:{s}</span>
            <span className="font-body text-[10px] uppercase tracking-[0.14em] text-[color:var(--slate-light)]">
                {pt.wizard.timerLabel}
            </span>
        </div>
    );
};

const WizardPage = () => {
    const navigate = useNavigate();
    const [startedAt] = useState(Date.now());
    const [step, setStep] = useState(1);

    // Customer
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [createNew, setCreateNew] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        customer_type: "individual",
        full_name: "",
        phone: "",
        email: "",
        bi_number: "",
        nif: "",
        address_street: "",
        address_city: "Luanda",
    });

    // Documents
    const [documents, setDocuments] = useState([]);
    const [docType, setDocType] = useState("bi");
    const [uploading, setUploading] = useState(false);
    const [extractingDoc, setExtractingDoc] = useState(null);  // doc being extracted in IDP view

    // Services
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);

    // Review
    const [priority, setPriority] = useState("medium");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    // Confirmation
    const [createdOrder, setCreatedOrder] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const list = await apiGet("/service-catalogue");
                setServices(list || []);
            } catch (e) {
                setServices([]);
            }
        })();
    }, []);

    useEffect(() => {
        if (step !== 1 || createNew) return;
        const id = setTimeout(async () => {
            try {
                const params = new URLSearchParams();
                if (customerSearch) params.set("search", customerSearch);
                params.set("page_size", "8");
                const data = await apiGet(`/customers?${params}`);
                setCustomers(data || []);
            } catch (e) {
                setCustomers([]);
            }
        }, 250);
        return () => clearTimeout(id);
    }, [customerSearch, step, createNew]);

    const canContinue = useMemo(() => {
        if (step === 1) return !!(selectedCustomer?.id);
        if (step === 3) return !!selectedService;
        return true;
    }, [step, selectedCustomer, selectedService]);

    const goNext = async () => {
        setError("");
        if (step === 1 && createNew) {
            if (!newCustomer.full_name || !newCustomer.phone) {
                setError(pt.wizard.validationNamePhone || "Nome e telefone são obrigatórios.");
                return;
            }
            try {
                const created = await apiPost("/customers", newCustomer);
                setSelectedCustomer(created);
                setCreateNew(false);
                setStep(2);
                return;
            } catch (e) {
                setError(e?.response?.data?.errors?.[0]?.message || pt.errors.generic);
                return;
            }
        }
        if (step === 4) {
            // Submit
            setSubmitting(true);
            try {
                const order = await apiPost("/service-orders", {
                    customer_id: selectedCustomer.id,
                    service_id: selectedService.id,
                    priority,
                    notes: notes || null,
                });
                // Link documents to this order
                for (const d of documents) {
                    // Best-effort: ignore failures
                    try { await api.patch(`/documents/${d.id}/review`, { is_verified: true }); } catch {}
                }
                setCreatedOrder(order);
                setStep(5);
            } catch (e) {
                setError(e?.response?.data?.errors?.[0]?.message || pt.errors.generic);
            } finally {
                setSubmitting(false);
            }
            return;
        }
        setStep((s) => Math.min(5, s + 1));
    };

    const goBack = () => setStep((s) => Math.max(1, s - 1));

    const uploadDoc = async (file) => {
        if (!selectedCustomer) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("customer_id", selectedCustomer.id);
            form.append("document_type", docType);
            form.append("file", file);
            const r = await api.post("/documents/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const doc = r.data.data;
            setDocuments((d) => [doc, ...d]);
        } catch (e) {
            setError(e?.response?.data?.errors?.[0]?.message || pt.errors.generic);
        } finally {
            setUploading(false);
        }
    };

    const extractDoc = (doc) => {
        setExtractingDoc(doc);
    };

    const handleExtractionComplete = (result) => {
        // Refresh the document in our list
        const updated = result?.document;
        if (updated) {
            setDocuments((arr) => arr.map((d) => (d.id === updated.id ? updated : d)));
        }
        setExtractingDoc(null);
    };

    return (
        <div data-testid="wizard-root">
            <PageHeader
                title={pt.services.new}
                subtitle={STEPS[step - 1]?.label}
                right={<Timer startedAt={startedAt} />}
            />

            <Stepper current={step} />

            {error && (
                <div className="mb-4 flex items-start gap-2 rounded-xl px-3.5 py-3 bg-[rgba(192,57,43,0.08)] border border-[rgba(192,57,43,0.25)]">
                    <AlertCircle size={16} className="text-[color:var(--error)] mt-0.5" />
                    <span className="text-sm text-[color:var(--error)]">{error}</span>
                </div>
            )}

            <AnimatePresence mode="wait">
                <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                >
                    {/* STEP 1: Customer */}
                    {step === 1 && (
                        <GlassCard padding="p-7" testId="wizard-step-1">
                            <h3 className="font-display text-2xl text-[color:var(--slate-deep)] mb-1">
                                {pt.wizard.step1Title}
                            </h3>
                            <p className="text-sm text-[color:var(--slate-mid)] mb-5">{pt.wizard.step1Sub}</p>

                            {!createNew ? (
                                <>
                                    <div className="flex items-center gap-2 mb-3">
                                        <GlassInput
                                            prefix={<Search size={16} />}
                                            placeholder={pt.wizard.searchCustomer}
                                            value={customerSearch}
                                            onChange={(e) => setCustomerSearch(e.target.value)}
                                            testId="wizard-customer-search"
                                        />
                                        <NxButton
                                            variant="secondary"
                                            onClick={() => setCreateNew(true)}
                                            icon={<Plus size={15} />}
                                            testId="wizard-new-customer"
                                        >
                                            {pt.wizard.newCustomer}
                                        </NxButton>
                                    </div>
                                    <ul className="space-y-1.5">
                                        {customers.length === 0 && (
                                            <li className="text-sm text-[color:var(--slate-mid)] py-4">
                                                Comece a digitar para procurar clientes…
                                            </li>
                                        )}
                                        {customers.map((c) => {
                                            const active = selectedCustomer?.id === c.id;
                                            return (
                                                <li
                                                    key={c.id}
                                                    className={
                                                        "rounded-xl p-3 cursor-pointer flex items-center justify-between transition-colors " +
                                                        (active
                                                            ? "bg-[color:var(--copper-subtle)] border border-[color:var(--copper-glow)]"
                                                            : "border border-transparent hover:bg-[color:var(--bg-surface)]")
                                                    }
                                                    onClick={() => setSelectedCustomer(c)}
                                                    data-testid={`customer-card-${c.id}`}
                                                >
                                                    <div>
                                                        <p className="font-body text-sm font-semibold text-[color:var(--slate-deep)]">
                                                            {c.full_name}
                                                        </p>
                                                        <p className="font-mono text-xs text-[color:var(--slate-mid)] mt-0.5">
                                                            {c.phone} · {c.customer_type === "individual" ? "Particular" : c.customer_type === "enterprise" ? "Empresa" : "Governo"}
                                                        </p>
                                                    </div>
                                                    {active && <Check className="text-[color:var(--copper)]" size={18} />}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <GlassInput label="Nome completo" value={newCustomer.full_name} onChange={(e) => setNewCustomer({ ...newCustomer, full_name: e.target.value })} required testId="wizard-new-name" />
                                    <GlassInput label="Telefone" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} required testId="wizard-new-phone" />
                                    <GlassInput label="Email" type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} testId="wizard-new-email" />
                                    <div>
                                        <label className="block text-xs uppercase tracking-wide text-[color:var(--slate-mid)] mb-1.5 font-medium">Tipo</label>
                                        <select className="glass-input" value={newCustomer.customer_type} onChange={(e) => setNewCustomer({ ...newCustomer, customer_type: e.target.value })} data-testid="wizard-new-type">
                                            <option value="individual">Particular</option>
                                            <option value="enterprise">Empresarial</option>
                                            <option value="government">Governo</option>
                                        </select>
                                    </div>
                                    <GlassInput label="BI" value={newCustomer.bi_number} onChange={(e) => setNewCustomer({ ...newCustomer, bi_number: e.target.value })} testId="wizard-new-bi" />
                                    <GlassInput label="NIF" value={newCustomer.nif} onChange={(e) => setNewCustomer({ ...newCustomer, nif: e.target.value })} testId="wizard-new-nif" />
                                    <GlassInput label="Morada" className="md:col-span-2" value={newCustomer.address_street} onChange={(e) => setNewCustomer({ ...newCustomer, address_street: e.target.value })} testId="wizard-new-address" />
                                    <div className="md:col-span-2 flex justify-end">
                                        <NxButton variant="secondary" onClick={() => setCreateNew(false)}>{pt.common.cancel}</NxButton>
                                    </div>
                                </div>
                            )}
                        </GlassCard>
                    )}

                    {/* STEP 2: Documents */}
                    {step === 2 && extractingDoc && (
                        <DocumentExtractionPanel
                            document={extractingDoc}
                            onComplete={handleExtractionComplete}
                            onCancel={() => setExtractingDoc(null)}
                        />
                    )}
                    {step === 2 && !extractingDoc && (
                        <GlassCard padding="p-7" testId="wizard-step-2">
                            <h3 className="font-display text-2xl text-[color:var(--slate-deep)] mb-1">
                                {pt.wizard.step2Title}
                            </h3>
                            <p className="text-sm text-[color:var(--slate-mid)] mb-5">{pt.wizard.step2Sub}</p>

                            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-4">
                                <select className="glass-input" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="wizard-doc-type">
                                    {DOC_TYPES.map((d) => (
                                        <option key={d.value} value={d.value}>{d.label}</option>
                                    ))}
                                </select>
                                <label className="btn-copper cursor-pointer inline-flex items-center gap-2" data-testid="wizard-upload-label">
                                    <Upload size={16} />
                                    {uploading ? "A carregar…" : pt.wizard.uploadDoc}
                                    <input
                                        type="file"
                                        className="hidden"
                                        onChange={(e) => {
                                            if (e.target.files?.[0]) uploadDoc(e.target.files[0]);
                                            e.target.value = "";
                                        }}
                                        data-testid="wizard-upload-input"
                                    />
                                </label>
                            </div>

                            <div
                                className="border-2 border-dashed rounded-2xl p-7 text-center mb-4"
                                style={{ borderColor: "var(--copper-glow)" }}
                            >
                                <Upload size={28} className="mx-auto text-[color:var(--copper)] mb-2" />
                                <p className="font-body text-sm text-[color:var(--slate-mid)]">
                                    Arraste ficheiros aqui ou utilize o botão acima.
                                </p>
                            </div>

                            <ul className="space-y-2">
                                {documents.map((d) => (
                                    <li key={d.id} className="glass-card p-3 flex items-center justify-between" data-testid={`wizard-doc-${d.id}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="h-9 w-9 rounded-md bg-[color:var(--copper-subtle)] grid place-items-center">
                                                <FileText size={16} className="text-[color:var(--copper-dark)]" />
                                            </div>
                                            <div>
                                                <p className="font-body text-sm font-medium text-[color:var(--slate-deep)]">{d.file_name}</p>
                                                <p className="font-mono text-xs text-[color:var(--slate-mid)]">
                                                    {(d.file_size / 1024).toFixed(1)} KB · {DOC_TYPES.find((t) => t.value === d.document_type)?.label}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {d.extraction_status === "completed" ? (
                                                <NxBadge variant="success"><CheckCircle2 size={12} /> {pt.wizard.documentReady}</NxBadge>
                                            ) : (
                                                <NxButton
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => extractDoc(d)}
                                                    icon={<Sparkles size={14} />}
                                                    testId={`extract-doc-${d.id}`}
                                                >
                                                    {pt.wizard.extractAi}
                                                </NxButton>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </GlassCard>
                    )}

                    {/* STEP 3: Service */}
                    {step === 3 && (
                        <GlassCard padding="p-7" testId="wizard-step-3">
                            <h3 className="font-display text-2xl text-[color:var(--slate-deep)] mb-1">
                                {pt.wizard.step3Title}
                            </h3>
                            <p className="text-sm text-[color:var(--slate-mid)] mb-5">{pt.wizard.step3Sub}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {services.map((s) => {
                                    const active = selectedService?.id === s.id;
                                    return (
                                        <button
                                            type="button"
                                            key={s.id}
                                            onClick={() => setSelectedService(s)}
                                            className={
                                                "text-left rounded-xl p-4 transition-all border " +
                                                (active
                                                    ? "border-[color:var(--copper)] bg-[color:var(--copper-subtle)] shadow-[0_0_0_4px_var(--copper-glow)]"
                                                    : "border-[color:var(--glass-border)] bg-[color:var(--bg-surface)] hover:border-[color:var(--copper-glow)]")
                                            }
                                            data-testid={`wizard-service-${s.id}`}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <h4 className="font-display text-base text-[color:var(--slate-deep)]">{s.name}</h4>
                                                {active && <Check size={16} className="text-[color:var(--copper)]" />}
                                            </div>
                                            <p className="font-body text-xs text-[color:var(--slate-mid)] mb-3 leading-snug">{s.description}</p>
                                            <div className="flex items-end justify-between">
                                                <span className="font-mono text-lg text-[color:var(--copper-dark)] font-medium">
                                                    AOA {formatAOAFull(s.price_aoa)}
                                                </span>
                                                <NxBadge variant="neutral">SLA {s.sla_activation_hours}h</NxBadge>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </GlassCard>
                    )}

                    {/* STEP 4: Review */}
                    {step === 4 && (
                        <GlassCard padding="p-7" testId="wizard-step-4">
                            <h3 className="font-display text-2xl text-[color:var(--slate-deep)] mb-1">
                                {pt.wizard.step4Title}
                            </h3>
                            <p className="text-sm text-[color:var(--slate-mid)] mb-5">{pt.wizard.step4Sub}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                                <div>
                                    <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)] mb-1">Cliente</p>
                                    <p className="font-display text-lg text-[color:var(--slate-deep)]">{selectedCustomer?.full_name}</p>
                                    <p className="font-mono text-sm text-[color:var(--slate-mid)]">{selectedCustomer?.phone}</p>
                                </div>
                                <div>
                                    <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)] mb-1">{pt.common.service}</p>
                                    <p className="font-display text-lg text-[color:var(--slate-deep)]">{selectedService?.name}</p>
                                    <p className="font-mono text-sm text-[color:var(--copper-dark)]">
                                        AOA {formatAOAFull(selectedService?.price_aoa)} · SLA {selectedService?.sla_activation_hours}h
                                    </p>
                                </div>
                                <div>
                                    <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)] mb-1">Documentos</p>
                                    <p className="font-body text-sm text-[color:var(--slate-deep)]">
                                        {documents.length} ficheiro{documents.length !== 1 ? "s" : ""} carregado{documents.length !== 1 ? "s" : ""}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs uppercase tracking-wide text-[color:var(--slate-mid)] mb-1.5 font-medium">{pt.wizard.priorityLabel}</label>
                                    <select className="glass-input" value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="wizard-priority">
                                        <option value="low">{pt.services.priority_low}</option>
                                        <option value="medium">{pt.services.priority_medium}</option>
                                        <option value="high">{pt.services.priority_high}</option>
                                        <option value="critical">{pt.services.priority_critical}</option>
                                    </select>
                                </div>
                                <GlassInput label={pt.wizard.notesLabel} value={notes} onChange={(e) => setNotes(e.target.value)} testId="wizard-notes" />
                            </div>
                        </GlassCard>
                    )}

                    {/* STEP 5: Confirmation */}
                    {step === 5 && createdOrder && (
                        <GlassCard padding="p-12" testId="wizard-step-5" className="text-center">
                            <motion.div
                                initial={{ scale: 0, rotate: -45 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: "spring", duration: 0.7 }}
                                className="mx-auto h-20 w-20 rounded-full bg-[color:var(--copper-subtle)] grid place-items-center mb-4"
                            >
                                <CheckCircle2 size={42} className="text-[color:var(--copper)]" />
                            </motion.div>
                            <h3 className="font-display text-3xl text-[color:var(--slate-deep)]">{pt.wizard.step5Title}</h3>
                            <p className="font-body text-sm text-[color:var(--slate-mid)] mt-2 max-w-md mx-auto">{pt.wizard.step5Sub}</p>
                            <div className="mt-5 inline-block">
                                <NxBadge variant="copper" data-testid="created-order-number">
                                    {createdOrder.order_number}
                                </NxBadge>
                            </div>
                            <div className="mt-7 flex justify-center gap-3">
                                <NxButton variant="secondary" onClick={() => navigate("/servicos")} testId="wizard-back-list">
                                    {pt.wizard.backToServices}
                                </NxButton>
                                <NxButton
                                    variant="copper"
                                    onClick={() => navigate(`/servicos/${createdOrder.id}`)}
                                    icon={<ArrowRight size={16} />}
                                    testId="wizard-view-order"
                                >
                                    {pt.wizard.viewOrder}
                                </NxButton>
                            </div>
                        </GlassCard>
                    )}
                </motion.div>
            </AnimatePresence>

            {step < 5 && !extractingDoc && (
                <div className="mt-5 flex justify-between">
                    <NxButton variant="secondary" onClick={goBack} disabled={step === 1} icon={<ChevronLeft size={16} />} testId="wizard-back">
                        {pt.wizard.back}
                    </NxButton>
                    <NxButton
                        variant="copper"
                        onClick={goNext}
                        disabled={!canContinue && !(step === 1 && createNew)}
                        loading={submitting}
                        icon={!submitting ? <ChevronRight size={16} /> : null}
                        testId="wizard-next"
                    >
                        {step === 4 ? pt.wizard.finish : pt.wizard.next}
                    </NxButton>
                </div>
            )}
        </div>
    );
};

export default WizardPage;
